/**
 * Offline Credential Service
 *
 * Caches credentials and qualification types to IndexedDB for offline access.
 * Read-only cache (credentials are not created/edited offline).
 */

import { db, CachedCredential, CachedQualificationType } from './db';
import { api } from '@/services/api';
import { photoCacheService } from './photo-cache.service';

export const offlineCredentialService = {
  /**
   * Cache credentials from server for offline use.
   * Fetches up to 500 credentials and stores them locally.
   */
  async cacheFromServer(tenantId: string): Promise<{ cached: number; photos: number }> {
    const result = { cached: 0, photos: 0 };

    try {
      const response = await api.get<{ data: any[]; total: number }>(
        '/credentials?entity_type=person&limit=500'
      );
      const credentials = response.data || [];

      await db.transaction('rw', db.cachedCredentials, async () => {
        // Clear existing cache for this tenant
        await db.cachedCredentials.where('tenant_id').equals(tenantId).delete();

        const records: CachedCredential[] = credentials.map((c: any) => ({
          id: c.id,
          tenant_id: tenantId,
          credential_number: c.credential_number || '',
          entity_type: c.entity_type || 'person',
          status: c.status || 'draft',
          display_name: c.display_name || '',
          photo_url: c.photo_url,
          expires_at: c.expires_at,
          first_name: c.person?.first_name,
          last_name: c.person?.last_name,
          email: c.person?.email,
          phone: c.person?.phone,
          organization: c.person?.organization,
          title: c.person?.title,
          department: c.person?.department,
          qualification_codes: (c.qualifications || [])
            .map((q: any) => q.code)
            .join(','),
          data: c,
          is_synced: true,
          last_synced_at: new Date().toISOString(),
        }));

        if (records.length > 0) {
          await db.cachedCredentials.bulkPut(records);
        }
        result.cached = records.length;
      });

      // Cache photo blobs for offline rendering
      const photoItems = credentials
        .filter((c: any) => c.photo_url)
        .map((c: any) => ({ id: c.id, photoUrl: c.photo_url }));
      result.photos = await photoCacheService.cachePhotos('credential', photoItems);
    } catch (error) {
      console.error('[Credentials] Failed to cache from server:', error);
    }

    return result;
  },

  /**
   * Get cached credentials for offline use.
   */
  async getCached(tenantId: string): Promise<CachedCredential[]> {
    return db.cachedCredentials
      .where('tenant_id')
      .equals(tenantId)
      .toArray();
  },

  /**
   * Get a single cached credential by ID.
   */
  async getCachedById(credentialId: string): Promise<CachedCredential | undefined> {
    return db.cachedCredentials.get(credentialId);
  },

  /**
   * Search cached credentials by display name or credential number.
   */
  async searchCached(tenantId: string, query: string): Promise<CachedCredential[]> {
    const lowerQuery = query.toLowerCase();
    return db.cachedCredentials
      .where('tenant_id')
      .equals(tenantId)
      .filter(
        (c) =>
          c.display_name.toLowerCase().includes(lowerQuery) ||
          c.credential_number.toLowerCase().includes(lowerQuery) ||
          (c.organization || '').toLowerCase().includes(lowerQuery)
      )
      .toArray();
  },

  /**
   * Cache qualification types for offline use.
   */
  async cacheQualificationTypes(): Promise<{ cached: number }> {
    const result = { cached: 0 };

    try {
      const response = await api.get<{ data: any[] }>(
        '/credentials/qualification-types'
      );
      const types = response.data || [];

      await db.transaction('rw', db.cachedQualificationTypes, async () => {
        await db.cachedQualificationTypes.clear();

        const records: CachedQualificationType[] = types.map((t: any) => ({
          id: t.id,
          code: t.code,
          name: t.name,
          category: t.category || '',
          description: t.description,
          level: t.level,
          nims_typed: t.nims_typed || false,
          sort_order: t.sort_order || 0,
          is_active: t.is_active !== false,
        }));

        if (records.length > 0) {
          await db.cachedQualificationTypes.bulkPut(records);
        }
        result.cached = records.length;
      });
    } catch (error) {
      console.error('[Credentials] Failed to cache qualification types:', error);
    }

    return result;
  },

  /**
   * Get cached qualification types.
   */
  async getCachedQualificationTypes(): Promise<CachedQualificationType[]> {
    return db.cachedQualificationTypes.toArray();
  },

  /**
   * Get count of cached credentials.
   */
  async getCachedCount(tenantId: string): Promise<number> {
    return db.cachedCredentials
      .where('tenant_id')
      .equals(tenantId)
      .count();
  },

  /**
   * Clear all cached credential data.
   */
  async clearCache(): Promise<void> {
    await db.cachedCredentials.clear();
    await db.cachedQualificationTypes.clear();
    await photoCacheService.clearCache('credential');
  },
};
